import type { Mode, Overlay } from "@/components/plumb/engine";

export const MAIL = "hey@genie.art";
export const LINKS = {
  app: "https://magiclamp.genie.art/projects",
  api: `mailto:${MAIL}?subject=API%20access`,
  collab: "mailto:hey@orkhan.art?subject=Collab",
};
export const FOUNDER = "Orkhan Mammadov";
/** What the studio calls itself, wherever it is named in full. */
export const NAME = "Genie studio of applied AI [ artistic intelligence. ]";

/**
 * Pictures and films are not in the repo. A build reads them from the server, where they sit beside
 * the desktop downloads; a dev machine keeps its own copy in `public/gaze`.
 */
export const MEDIA = import.meta.env.PROD ? "/download/studio" : "/gaze";

/**
 * The atmosphere pictures, none of them studio artwork: flash photographs of an
 * underground techno night, black with one acid light.
 */
const img = (name: string) => `${MEDIA}/under/${name}.jpg`;
/** Studio work, shown only in the case studies. */
const work = (name: string) => `${MEDIA}/work/${name}.jpg`;
/** Every picture has a depth map beside it: Depth Anything v2 for the work, an estimate from the flash falloff for the rest. */
export const depthOf = (src: string) => src.replace(/\/(under|work|people)\//, "/$1/depth/");

export const HERO = {
  src: img("hero"),
  title: ["Genie studio of", "applied AI*"],
  /** The footnote the asterisk points to, set small under the title. */
  gloss: "*[ artistic intelligence. ]",
  first: "we create generative media, and we train the models and build the workflows and tools behind it since 2016.",
  hint: "move over the picture / drag a window / press to change the reading",
  /** The reading the window under the pointer starts with: an index into EFFECTS. */
  loupe: 3,
  by: { company: "GenieArt Inc.", founderUrl: "https://orkhan.art", place: "Based in New York, US" },
};

/**
 * Windows that drift over the hero picture; each starts on one reading (an index into EFFECTS) of what is under it.
 * They stand around the face, on the shoulders, the ear and the neck, so the glasses and the grin stay clear.
 */
export interface Lens {
  id: string;
  reading: number;
  /** Position and size as fractions of the band. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Drift, in band widths, and how long one pass takes. */
  dx: number;
  dy: number;
  seconds: number;
}

export const LENSES: Lens[] = [
  { id: "a", reading: 0, x: 0.05, y: 0.3, w: 0.18, h: 0.6, dx: 0.03, dy: -0.02, seconds: 17 },
  { id: "b", reading: 2, x: 0.3, y: 0.06, w: 0.12, h: 0.34, dx: -0.02, dy: 0.03, seconds: 13 },
  { id: "c", reading: 10, x: 0.67, y: 0.26, w: 0.16, h: 0.52, dx: 0.02, dy: 0.01, seconds: 21 },
  { id: "d", reading: 11, x: 0.55, y: 0.66, w: 0.1, h: 0.3, dx: -0.02, dy: -0.02, seconds: 19 },
];

/** Who the studio has worked with. Marks and wordmarks are the companies' own, from the LobeHub icon set. */
export const WORKED_WITH: Array<{ name: string; logo: string }> = [
  { name: "OpenAI", logo: "openai" },
  { name: "ComfyUI", logo: "comfyui" },
  { name: "Nous Research", logo: "nousresearch" },
  { name: "fal.ai", logo: "fal" },
];

export const STATEMENT = {
  label: "what we do",
  text: "we work for brands, artists and institutions. we make the film, the image or the installation, then hand over the model and the tool that made it.",
};

/** What the workflows and tools are built with. */
/** A tool we build with: its mark, and its wordmark where the maker has one. */
export interface Tool {
  name: string;
  use: string;
  marks: string[];
  word?: string;
  /** A two-line lockup, set a little taller than the rest. */
  tall?: boolean;
}

const logo = (name: string) => `/logos/${name}`;

export const ARSENAL: Tool[] = [
  {
    name: "ComfyUI",
    use: "node graphs and custom nodes around image and video models.",
    marks: [logo("comfyui.svg")],
    word: logo("comfyui-text.svg"),
  },
  {
    name: "TouchDesigner",
    use: "real-time and audio-reactive visuals, installation.",
    marks: [],
    word: logo("touchdesigner.svg"),
    tall: true,
  },
  {
    name: "WebGL & WebGPU",
    use: "shaders and rendering in the browser.",
    marks: [logo("webgl.svg"), logo("webgpu.svg")],
  },
  { name: "Three.js", use: "3d scenes on the web.", marks: [logo("threejs.svg")] },
  { name: "Runway", use: "video generation.", marks: [logo("runway.svg")], word: logo("runway-text.svg") },
  { name: "Flora", use: "a canvas for chaining models.", marks: [logo("flora.svg")], word: logo("flora-text.svg") },
  { name: "Magnific", use: "upscaling and detail.", marks: [logo("magnific.png")] },
  { name: "Flux", use: "image models and fine-tunes.", marks: [logo("flux.svg")], word: logo("flux-text.svg") },
  {
    name: "Stability AI",
    use: "stable diffusion, trained on one body of work.",
    marks: [logo("stability.svg")],
    word: logo("stability-text.svg"),
  },
  { name: "Kling", use: "video models.", marks: [logo("kling.svg")], word: logo("kling-text.svg") },
  { name: "Luma", use: "video and 3d capture.", marks: [logo("luma.svg")], word: logo("luma-text.svg") },
  {
    name: "Ideogram",
    use: "pictures with type in them.",
    marks: [logo("ideogram.svg")],
    word: logo("ideogram-text.svg"),
  },
  {
    name: "ElevenLabs",
    use: "voice and sound.",
    marks: [logo("elevenlabs.svg")],
    word: logo("elevenlabs-text.svg"),
  },
  { name: "fal", use: "running models at scale.", marks: [logo("fal.svg")], word: logo("fal-text.svg") },
];

/** One of the studio's three wishes: the plain word for it, and the wish. */
export interface Wish {
  word: string;
  text: string;
}

export const ABOUT = {
  label: NAME,
  title: "Applied AI ™ 2016–2026",
  intro: "every genie grants three wishes. these are ours.",
  hint: "scroll to turn the genie",
  /** One wish to a genie, in the order the genies turn up. */
  wishes: [
    {
      word: "vision",
      text: "a machine for every culture of images. the carpet, the miniature, the old masters, yours.",
    },
    {
      word: "mission",
      text: "nothing we make stays locked in the studio. we make the film, train the model, and hand over the tool that made it.",
    },
    {
      word: "goals",
      text: "by the time genie turns twenty, anyone can open it and run what took us ten years to build.",
    },
  ] as Wish[],
  genies: ["genie-1", "genie-2", "genie-3"].map(name => `/genie-mark/${name}.svg`),
};

export const APP = {
  text: "everything we build for our own work ends up in genie. it runs online: open it and use the same tools we do.",
  bench: "one picture, eight readings. switch them:",
};

export interface Chapter {
  id: string;
  no: string;
  title: string;
  /** The word used in the call to action: start a … project. */
  kind: string;
  headline: string;
  body: string;
  index: string[];
  out: string;
  tone: "orange" | "pink" | "lilac" | "acid";
  src: string;
  mode: Mode;
  overlay: Overlay | "none";
}

export const CHAPTERS: Chapter[] = [
  {
    id: "media",
    no: "01",
    title: "Media",
    kind: "media",
    headline: "film, stills, real-time, installation.",
    body: "we direct it and finish it like any other production. if it has to hold up on a ten-metre wall, it will.",
    index: [
      "film and moving image",
      "stills and campaigns",
      "real-time and audio-reactive visuals",
      "installation, projection, led",
    ],
    out: "masters, loops, real-time builds",
    tone: "orange",
    src: img("station"),
    mode: "photo",
    overlay: "tracery",
  },
  {
    id: "models",
    no: "02",
    title: "Models",
    kind: "model",
    headline: "a model trained for one look, yours to keep.",
    body: "when no existing model gets the picture right, we train one: curated, captioned and tested. you keep the weights.",
    index: [
      "dataset curation and captioning",
      "diffusion fine-tunes and gans",
      "motion and video models",
      "segmentation, pose, depth, tracking",
    ],
    out: "weights, dataset, eval sheet",
    tone: "pink",
    src: img("feed"),
    mode: "photo",
    overlay: "detect",
  },
  {
    id: "workflows",
    no: "03",
    title: "Workflows",
    kind: "workflow",
    headline: "the graph behind the picture, built to run again.",
    body: "built in comfyui and touchdesigner: models, custom nodes and controls wired so the same result comes out every time.",
    index: [
      "node graphs and custom nodes",
      "control from pose, depth, masks",
      "real-time and browser pipelines",
      "batch and pipeline automation",
    ],
    out: "the graph, documented, yours to run",
    tone: "lilac",
    src: img("p04"),
    mode: "photo",
    overlay: "windows",
  },
  {
    id: "tools",
    no: "04",
    title: "Tools",
    kind: "tool",
    headline: "the workflows that hold up, shipped as tools online.",
    body: "everything in genie started as a workflow for one of our own projects. we build the same for teams: an app they open, or an api they call.",
    index: [
      "genie, online in the browser",
      "custom tools built on a workflow",
      "apis and endpoints",
      "handover and training for your team",
    ],
    out: "app, endpoint, or the graph itself",
    tone: "acid",
    src: img("p07"),
    mode: "photo",
    overlay: "fields",
  },
];

export interface Case {
  id: string;
  code: string;
  title: string;
  meta: string;
  body: string;
  facts: Array<[string, string]>;
  src: string;
  /** A short loop cut from the case film, playing behind its panel on the studio page. */
  loop: string;
}

export const CASES: Case[] = [
  {
    id: "eastern-promises",
    code: "GN-01EP",
    title: "Eastern Promises",
    meta: "2023 · Art Dubai",
    body: "8,000 orientalist paintings went in. ten metres of curved led came out.",
    facts: [
      ["in", "8,000 orientalist paintings"],
      ["out", "10 m curved led wall"],
      ["shown", "art dubai, 2023"],
    ],
    src: work("eastern"),
    loop: `${MEDIA}/work/loops/eastern-promises.mp4`,
  },
  {
    id: "muraqqa",
    code: "GN-02MQ",
    title: "Muraqqa",
    meta: "111 data miniatures",
    body: "miniature painting from azerbaijan, india, iran and turkey, taught to a machine. 111 came back.",
    facts: [
      ["in", "miniature painting, four traditions"],
      ["out", "111 data miniatures"],
    ],
    src: work("muraqqa"),
    loop: `${MEDIA}/work/loops/muraqqa.mp4`,
  },
  {
    id: "visions",
    code: "GN-03VS",
    title: "Visions",
    meta: "2025 · trilogy",
    body: "10,000 public-domain paintings, cut down to 666. a model that looks at old masters and misremembers them.",
    facts: [
      ["in", "10,000 paintings, cut to 666"],
      ["out", "a trilogy"],
      ["licensed", "openai, 2025"],
      ["with", "slava rybin, who trained the model"],
    ],
    src: work("visions"),
    loop: `${MEDIA}/work/loops/visions.mp4`,
  },
  {
    id: "saving-aesthetics",
    code: "GN-04SA",
    title: "Saving Aesthetics",
    meta: "2019 · Moscow Biennale",
    body: "a gan that learned azerbaijani carpets, projected onto a blank white one.",
    facts: [
      ["in", "azerbaijani carpets"],
      ["out", "projection on a blank white carpet"],
      ["shown", "moscow biennale, 2019"],
      ["collected", "state tretyakov gallery"],
    ],
    src: work("saving"),
    loop: `${MEDIA}/work/loops/saving-aesthetics.mp4`,
  },
  {
    id: "paul-and-shark",
    code: "GN-05PS",
    title: "Paul & Shark",
    meta: "brand collaboration",
    body: "the same method, for an italian clothing house.",
    facts: [
      ["for", "paul & shark"],
      ["film", "the a.i. odyssey"],
    ],
    src: work("paulshark"),
    loop: `${MEDIA}/work/loops/paul-and-shark.mp4`,
  },
];

export const BENCH_SRC = img("plate");
export const MODES: Array<{ id: Mode; overlay: Overlay | "none"; label: string }> = [
  { id: "photo", overlay: "tracery", label: "tracery" },
  { id: "photo", overlay: "detect", label: "detect" },
  { id: "photo", overlay: "fields", label: "fields" },
  { id: "photo", overlay: "windows", label: "scan" },
  { id: "depth", overlay: "none", label: "depth" },
  { id: "edge", overlay: "none", label: "edge" },
  { id: "glyph", overlay: "none", label: "glyph" },
  { id: "block", overlay: "none", label: "block" },
];

/** How soon a project is wanted; the last question of the brief. */
export const WHEN = ["this month", "in 1 to 3 months", "later this year", "not sure yet"];

/** One work in the gallery: a film, shown whole at its own width over height. */
export interface Clip {
  video: string;
  label: string;
  ratio: number;
}

const film = (file: string) => `https://orkhan.art/media/original/user/admin/${file}-video.mp4`;

/** Clips he sent as files; they sit beside the other studio work pictures. */
const clip = (n: string) => `${MEDIA}/work/clips/clip-${n}.mp4`;

const WIDE = 16 / 9;
const TALL = 9 / 16;

/** Studio work, in the order it is laid into rows. */
export const CLIPS: Clip[] = [
  { video: "https://orkhan.art/api/artworkvideo/file/baalbek-1-video.mp4", label: "baalbek", ratio: WIDE },
  {
    video: film("2e1412b0484a46ae9f9488c13b0552b1.the-beginning-and-the-end-001-1"),
    label: "the beginning and the end 001",
    ratio: TALL,
  },
  { video: clip("01"), label: "visions", ratio: TALL },
  { video: clip("04"), label: "eastern promises", ratio: TALL },
  {
    video: film("47a6ee46da2844afb422ab6b1978c289.relic-data-threads-7"),
    label: "relic data threads",
    ratio: WIDE,
  },
  {
    video: film("8ad7a282f29d496cab3fefca8b4e4333.revival-of-aesthetics-chapter-3-artwork-3-10"),
    label: "revival of aesthetics, chapter 3",
    ratio: TALL,
  },
  { video: clip("06"), label: "dataset loading", ratio: 4 / 5 },
  { video: clip("02"), label: "saving aesthetics", ratio: TALL },
  { video: clip("07"), label: "touchdesigner network", ratio: 1 },
  { video: clip("05"), label: "installation, calligraphy", ratio: TALL },
  { video: film("afca52e710494366b4309307eac35b9c.relic-data-carpet-4"), label: "relic data carpet", ratio: WIDE },
  { video: clip("03"), label: "installation, mirrors", ratio: TALL },
  { video: clip("09"), label: "edge detection", ratio: TALL },
  { video: clip("08"), label: "old masters, study", ratio: 1 },
  { video: clip("10"), label: "muraqqa, dataset", ratio: TALL },
  { video: clip("12"), label: "installation, image room", ratio: 1 },
  { video: clip("11"), label: "particles, study", ratio: TALL },
];

/** Each name has a picture for its card, and a reading (an index into EFFECTS) laid over it unless the picture is its own. */
export const COLLABORATORS: Array<{
  name: string;
  note: string;
  url: string;
  src: string;
  reading?: number;
}> = [
  {
    name: "Slava Rybin",
    note: "systems artist. trained the model for visions.",
    url: "https://www.rybinfx.com",
    src: `${MEDIA}/people/slava-rybin.png`,
  },
  {
    name: "Artur Gadzhiev",
    note: "",
    url: "https://www.instagram.com/art.gadzhiev/",
    src: `${MEDIA}/people/artur-gadzhiev.jpg`,
    reading: 9,
  },
  {
    name: "Andrey Flat",
    note: "media artist. procedural systems, generative effects, simulations.",
    url: "https://andreyflat.space",
    src: `${MEDIA}/people/andrey-flat.jpg`,
    reading: 4,
  },
  {
    name: "Marcin Ignac",
    note: "data artist. founder of variable.",
    url: "http://marcinignac.com",
    src: `${MEDIA}/people/marcin-ignac.jpg`,
    reading: 7,
  },
];
